import React, {useEffect, useState} from "react";
import { useDispatch, useSelector } from "react-redux";
import { CartesianGrid, Legend, LineChart, XAxis, YAxis, Line, ResponsiveContainer, Tooltip, Brush } from "recharts";
import RootState from "../../../redux/states/root.state";
import { AppDispatch } from "../../../redux/store";
import { fetchSpaHistory } from "../../../redux/actions/spaHistory.actions";
import ReportLoadingProgress from "./ReportLoadingProgress";
import { LiveReportLabels, PumpState, SBConState, filterStateMapper } from "../reportEnums.enums";
import {clearSpaHistory} from "../../../redux/reducers/spaHistory.reducer";
import { ActiveCycleStyleMap } from "../../../mqttData/live/heatCycle.enums";
import { SmartPHStateTextMap } from "../../../mqttData/live/smartPhState.enums";
import { useReportTimeFormat } from "../reportTime";
import { liveOptions } from "../options";
import useIsDarkTheme from "../../../hooks/useIsDarkTheme";

interface ReportsGraphProps {
  spa_guid: string;
  selected: any[];
  startDate: string;
  endDate: string;
}

// Validated categorical palette (dataviz reference: CVD-safe for adjacent lines), in fixed
// order, with its own steps for the dark surface. Past eight series the hues repeat with a
// dash pattern, so no two lines share colour *and* pattern and no hue is ever made up.
const SERIES_LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948'];
const SERIES_DARK = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'];
const DASHES = [undefined, '6 4', '2 3'];

// Slots follow the fields' fixed order in the picker, not click order, so a given set of
// fields always draws in the same colours
const fieldOrder = liveOptions.map((opt: any) => opt.id);
const seriesSlots = (selected: string[]) => {
  const ordered = [...selected].sort((a, b) => fieldOrder.indexOf(a) - fieldOrder.indexOf(b));
  return (field: string) => ordered.indexOf(field);
};

const CustomTooltip: React.FC<{ active:any, payload:any, label:any }> = ({ active, payload, label }) => {
  const formatTime = useReportTimeFormat();
  if (active && payload?.length) {
    return (
      <div className="rp-tooltip">
        <span className="rp-tooltip-time">{formatTime(label)}</span>
        {payload.map((el:any, i:number) => (
          <div key={i} className="rp-tooltip-row">
            <span className="rp-swatch" style={{background: el.color}} aria-hidden="true"/>
            <span className="rp-tooltip-name">{el.name}</span>
            <span className="rp-tooltip-value">{
                el.dataKey==='P1' ? PumpState[el.value] :
                el.dataKey==='SBConnected' ? SBConState[el.value] :
                el.dataKey==='Filter' ? (filterStateMapper[el.value as keyof typeof filterStateMapper] ?? el.value) :
                el.dataKey==='H1' ? ActiveCycleStyleMap[el.value as number] :
                el.dataKey==='phSM' ? SmartPHStateTextMap[el.value as keyof typeof SmartPHStateTextMap] :
                el.dataKey==='sbProducing' ? (el.value || el.value === 1 ? 'ON' : 'OFF') :
                el.value
              }
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const ReportsGraph: React.FC<ReportsGraphProps> = ({spa_guid, selected, startDate, endDate}) => {
  const dispatch: AppDispatch = useDispatch();
  const {data: live, loading, lastQuery} = useSelector((state: RootState) => state.spaHistory);
  const formatTime = useReportTimeFormat();
  const palette = useIsDarkTheme() ? SERIES_DARK : SERIES_LIGHT;
  const slotOf = seriesSlots(selected);
  const lineStyle = (field: string) => {
    const slot = Math.max(0, slotOf(field));
    return {stroke: palette[slot % palette.length], strokeDasharray: DASHES[Math.floor(slot / palette.length) % DASHES.length]};
  };

  useEffect(() => {
    let keys = [...selected];
    if (keys.includes('Current')){
      let i = keys.indexOf('Current');
      keys[i] = '#cr';
    }
    if (keys.includes('Filter')){
      let i = keys.indexOf('Filter');
      keys[i] = '#fl';
    }

    if (spa_guid){
      const spaInfo = {guid:spa_guid, date_from:new Date(startDate).getTime(), date_to:new Date(endDate).getTime(), keys:keys, type:'live'};

      // Skip the refetch when redux already holds this exact report — e.g. toggling
      // back to graph view of live data we just loaded for the table.
      if (lastQuery === JSON.stringify(spaInfo) && (live.length > 0 || loading)) {
        return;
      }

      dispatch(clearSpaHistory());
      dispatch(fetchSpaHistory(spaInfo));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, spa_guid, selected, startDate, endDate]);

  const [data, setData] = useState<any[]>([]);

  useEffect(() => {
    let newArray = [...live];
    newArray = newArray.map((data:any)=>{
      let keys = Object.keys(data);
      let newObj = {...data};
      if (keys.includes('SBConnected')) {
        newObj={...newObj, SBConnected: + data.SBConnected}
      }

      if (keys.includes('sbpH')) {
        newObj={...newObj, sbpH: data.sbpH/100}
      }

      if (keys.includes('Current')) {
        newObj={...newObj, Current: data.Current/100}
      }

      return {...newObj, timestamp: new Date(data.timestamp).getTime()}
    })

    newArray.sort((a:any,b:any)=>{
      return a.timestamp - b.timestamp
    })

    // Check if endDate is today
    const end = new Date(endDate);
    const now = new Date();
    const isToday = end.getFullYear() === now.getFullYear() &&
                    end.getMonth() === now.getMonth() &&
                    end.getDate() === now.getDate();

    // Add flat extension to current time if needed and endDate is today
    if (isToday && newArray.length > 0 && newArray[newArray.length - 1].timestamp < now.getTime()) {
      const last = newArray[newArray.length - 1];
      newArray.push({ ...last, timestamp: now.getTime() });
    }

    setData(newArray);
  }, [live,setData,endDate]);

  // Adaptive loader: overlay the "Rendering graph…" spinner on the chart and remove it
  // one frame *after* the chart actually paints, with a small minimum so the caption stays
  // readable on fast reports. `readyLive` is reset synchronously when the source data
  // changes so the spinner re-shows in the same render the data lands (no blank flash).
  const MIN_SPINNER_MS = 300;
  const [painted, setPainted] = useState(false);
  const [minElapsed, setMinElapsed] = useState(false);
  const [readyLive, setReadyLive] = useState<any>(null);
  if (live !== readyLive) {
    setReadyLive(live);
    setPainted(false);
    setMinElapsed(false);
  }
  // Small minimum so the loader + caption are actually legible.
  useEffect(() => {
    const t = setTimeout(() => setMinElapsed(true), MIN_SPINNER_MS);
    return () => clearTimeout(t);
  }, [readyLive]);
  // Detect the real paint: after `data` is committed the chart renders synchronously and
  // the browser paints it; the following frame's rAF then fires, so we know it's on screen.
  useEffect(() => {
    if (data.length === 0) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setPainted(true));
    });
    return () => { cancelAnimationFrame(raf1); if (raf2) cancelAnimationFrame(raf2); };
  }, [data]);
  const showSpinner = !(painted && minElapsed);

  // Middle ground: animate the line draw-in only for small datasets. On dense months
  // the enter-animation stutters and re-runs on every toggle, so we skip it there.
  const ANIMATE_MAX_POINTS = 500;
  const animateLines = data.length <= ANIMATE_MAX_POINTS;

  return (
    <>
      <div className="rp-graph">
        {loading ?
        <ReportLoadingProgress/>
        :
        live.length>0?
        ( <div className="rp-graph-frame">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart 
            data={data}
            margin={{ top: 16, right: 24, left: 24, bottom: 8 }}
          >
            <CartesianGrid vertical={false} />
            <XAxis 
              dataKey="timestamp"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={(timestamp) => formatTime(timestamp)}
              scale="time"
            />
            {/* Shared YAxis for temperature lines */}
            {(() => {
              const tempKeys = ['HTemp', 'STemp', 'TSP'];
              const selectedTempKeys = selected.filter((s:any) => tempKeys.includes(s));
              if (selectedTempKeys.length > 0) {
                const tempValues = data.flatMap((d:any) => tempKeys.map(k => d[k])).filter((v:any) => v !== undefined);
                const tempMin = Math.min(...tempValues, 57);
                const tempMax = Math.max(...tempValues, 57);
                return (
                  <YAxis
                    yAxisId="temp"
                    domain={[tempMin, tempMax * 1.1]}
                    scale="linear"
                    axisLine={false}
                    tick={false}
                    hide
                    allowDataOverflow={true}
                  />
                );
              }
              return null;
            })()}
            {/* Other YAxes for non-temperature lines */}
            {selected.map((select:any, i)=>
              ['sbVout'].includes(select) ?
                <YAxis 
                  key={i}
                  yAxisId={select}
                  domain={[0, 15000]}
                  scale="linear"
                  axisLine={false} tick={false} hide
                  allowDataOverflow={true}
                />
              :
              ['sbI1','sbI2'].includes(select) ?
                <YAxis 
                  key={i}
                  yAxisId={select}
                  domain={[0, 25000]}
                  scale="linear"
                  axisLine={false} tick={false} hide
                  allowDataOverflow={true}
                />
              :
              !['HTemp','STemp','TSP'].includes(select) ?
                <YAxis 
                  yAxisId={select}
                  domain={[(dataMin:any) => Math.max(0, dataMin * 0.9), 
                          (dataMax:any) => dataMax * 1.1]}            
                  axisLine={false} 
                  tick={false} 
                  hide 
                  scale="linear" 
                  allowDataOverflow={true}
                />
              : null
            )}
            <Tooltip
              content={<CustomTooltip active={false} payload={[]} label={""} />}
            />
            <Legend
              verticalAlign="top"
              height={36}
              iconType="plainline"
              formatter={(value) => <span className="rp-legend-text">{value}</span>}
            />
            {/* Render lines, assign temp lines to shared axis */}
            {selected.map((select:any, i)=>
              ['HTemp','STemp','TSP'].includes(select) ?
                <Line isAnimationActive={animateLines} connectNulls={true}  key={i} type="stepAfter" name={LiveReportLabels[select as keyof typeof LiveReportLabels]} dataKey={select} {...lineStyle(select)} strokeWidth={2} dot={false} activeDot={{r: 4, strokeWidth: 2}} yAxisId="temp"/>
              :
                <Line isAnimationActive={animateLines} connectNulls={true}  key={i} type="stepAfter" name={LiveReportLabels[select as keyof typeof LiveReportLabels]} dataKey={select} {...lineStyle(select)} strokeWidth={2} dot={false} activeDot={{r: 4, strokeWidth: 2}} yAxisId={select}/>
            )}
            {/* Drag the handles to zoom into a window; drag the window to scroll across the range */}
            <Brush
              dataKey="timestamp"
              height={28}
              travellerWidth={8}
              tickFormatter={() => ''} // no labels needed — the X-axis already shows date + time
            />
          </LineChart>
        </ResponsiveContainer>
        {showSpinner &&
        <div className="rp-render-overlay">
          <span className="spinner" role="progressbar" aria-label="Rendering graph"/>
          <span className="rp-progress-text">Rendering graph…</span>
        </div>
        }
        </div>
        )
        :
        <div className="dt-state"><div className="dt-empty-title">No Data Found</div></div>
        }
      </div>
    </>
  );
};

export default ReportsGraph;
